import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

const appLinks = [
  { to: "/", key: "home" },
  { to: "/matches", key: "matches" },
  { to: "/predict", key: "predict" },
  { to: "/standings", key: "standings" },
  { to: "/profile", key: "profile" },
] as const;

const discoverLinks = [
  { to: "/about", key: "about" },
  { to: "/how-it-works", key: "howItWorks" },
  { to: "/scoring-rules", key: "scoringRules" },
  { to: "/special-bets", key: "specialBets" },
  { to: "/blog", key: "blog" },
  { to: "/help", key: "help" },
  { to: "/contact", key: "contact" },
] as const;

const legalLinks = [
  { to: "/terms", key: "terms" },
  { to: "/privacy", key: "privacy" },
  { to: "/cookies", key: "cookies" },
  { to: "/credits", key: "credits" },
] as const;

export function Footer({ variant = "default" }: { variant?: "default" | "dark" }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();

  /** Fire-and-forget click log so we can see which footer links people use. */
  const trackClick = (linkKey: string, to: string, section: string) => {
    void supabase
      .from("footer_link_clicks")
      .insert({
        link_key: linkKey,
        path: to,
        from_path: location.pathname,
        section,
        variant,
        lang: i18n.language?.slice(0, 2) ?? null,
        user_id: user?.id ?? null,
      })
      .then(undefined, () => {
        /* tracking must never break navigation */
      });
  };

  const handleClick = (linkKey: string, to: string, section: string) => {
    trackClick(linkKey, to, section);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isDark = variant === "dark";

  const sectionTitle = (label: string) => (
    <h3
      className={cn(
        "mb-3 text-sm font-semibold tracking-wide",
        isDark ? "text-pitch-cream" : "text-foreground"
      )}
    >
      {label}
    </h3>
  );

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "block text-sm transition-colors",
      isDark
        ? "text-pitch-cream/60 hover:text-pitch-cream"
        : "text-muted-foreground hover:text-foreground",
      isActive && (isDark ? "text-pitch-cream" : "text-foreground")
    );

  const renderLinks = (links: readonly { to: string; key: string }[], section: string) =>
    links.map(({ to, key }) => (
      <li key={key}>
        <NavLink
          to={to}
          end={to === "/"}
          className={linkClass}
          onClick={() => handleClick(key, to, section)}
        >
          {t(`footer.links.${key}`)}
        </NavLink>
      </li>
    ));

  return (
    <footer
      className={cn(
        "border-t mt-auto",
        isDark
          ? "border-pitch bg-gradient-hero"
          : "border-border bg-muted/30"
      )}
    >
      <div className="container max-w-5xl px-4 py-8 md:py-10">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 font-bold" onClick={() => handleClick("brand", "/", "brand")}>
              <img
                src="/icon-512.png?v=2"
                alt="Tipomatic"
                className={cn("h-8 w-8 rounded-lg object-cover", isDark && "ring-1 ring-white/20")}
              />
              <span className={isDark ? "text-pitch-cream" : "text-foreground"}>Tipomatic</span>
            </Link>
            <p
              className={cn(
                "mt-3 text-sm leading-relaxed",
                isDark ? "text-pitch-cream/60" : "text-muted-foreground"
              )}
            >
              Built with 💚 for friends
            </p>
          </div>

          <div>
            {sectionTitle(t("footer.app"))}
            <ul className="space-y-2">{renderLinks(appLinks, "app")}</ul>
          </div>

          <div>
            {sectionTitle(t("footer.discover"))}
            <ul className="space-y-2">{renderLinks(discoverLinks, "discover")}</ul>
          </div>

          <div>
            {sectionTitle(t("footer.legal"))}
            <ul className="space-y-2">{renderLinks(legalLinks, "legal")}</ul>
          </div>
        </div>

        <div
          className={cn(
            "mt-8 flex flex-col items-center justify-between gap-3 border-t pt-6 text-sm md:flex-row",
            isDark ? "border-pitch text-pitch-cream/40" : "border-border text-muted-foreground"
          )}
        >
          <p>© {new Date().getFullYear()} Tipomatic</p>
          <p>Built with 💚 for friends</p>
        </div>
      </div>
    </footer>
  );
}
